import { z } from 'zod'
import type { Option } from '@/components/BaseCombobox.vue'
import type { FormContext } from 'vee-validate'
import type { FieldVisibility } from '@/models/patients'
import { ContentTypeModelName } from '@/models/customFields.js'
import { formatDate } from '@/utils/date/conversion.js'
import { useCustomFieldsForModel } from '@/composables/customFields.js'
import { useCurrentLocaleDateFormat } from '@/composables/i18next.js'
import {
  patientSex,
  type PatientCreate,
  type PatientSex,
  useEnumSexTranslation,
} from '@/composables/patients.js'
import { useSpeciesQuery } from '@/composables/species.js'
import { useAuth } from '@/composables/useAuth.js'
import { useFormValidation } from '@/composables/useFormValidation.js'
import useTranslation from '@/composables/useTranslation.js'
import type { PatientCPRStatus } from '@/composables/patientCPRStatus.js'
export type PatientDetailsFormSchema = {
  name: string
  officialName?: string
  avatar?: string
  type: string
  subtype?: string[]
  statuses?: string[]
  reasonOfDeath?: string
  dateOfDeath?: string
  cprStatus?: PatientCPRStatusValues
  dateOfBirth: string
  species: string
  breed: string
  gender?: PatientSex
  bloodGroup?: PatientBloodGroupValues
  color?: string
  additionalIdentifier?: string
  isPrivate?: boolean
  visibleForDepartments?: string[]
  visibleForDepartmentGroups?: string[]
  primaryClinicLocation?: string
  farmStableName?: string
  farmStableLocation?: string
  idNumber?: string
  customFields?: Record<string, string>
  nonFieldErrors?: string
} & PatientIdentificationValues

export type PatientIdentificationValues = {
  microchip?: string
  microchipDate?: string
  microchipPlacement?: string
  tagId?: string
  tagIdDate?: string
  tagIdPlacement?: string
  passport?: string
  passportDate?: string
  earNotched?: string
  earNotchedDate?: string
  earNotchedPlacement?: string
  tattoedId?: string
  tattoedIdDate?: string
  tattoedIdPlacement?: string
  freezeMarking?: string
  freezeMarkingDate?: string
  freezeMarkingPlacement?: string
  hotBrandedId?: string
  hotBrandedIdDate?: string
  hotBrandedIdPlacement?: string
  paintBranding?: string
  paintBrandingDate?: string
  paintBrandingPlacement?: string
}

export enum PatientIdentificationFields {
  Microchip = 'microchip',
  MicrochipDate = 'microchipDate',
  MicrochipPlacement = 'microchipPlacement',
  TagId = 'tagId',
  TagIdDate = 'tagIdDate',
  TagIdPlacement = 'tagIdPlacement',
  Passport = 'passport',
  PassportDate = 'passportDate',
  EarNotched = 'earNotched',
  EarNotchedDate = 'earNotchedDate',
  EarNotchedPlacement = 'earNotchedPlacement',
  TattoedId = 'tattoedId',
  TattoedIdDate = 'tattoedIdDate',
  TattoedIdPlacement = 'tattoedIdPlacement',
  FreezeMarking = 'freezeMarking',
  FreezeMarkingDate = 'freezeMarkingDate',
  FreezeMarkingPlacement = 'freezeMarkingPlacement',
  HotBrandedId = 'hotBrandedId',
  HotBrandedIdDate = 'hotBrandedIdDate',
  HotBrandedIdPlacement = 'hotBrandedIdPlacement',
  PaintBranding = 'paintBranding',
  PaintBrandingDate = 'paintBrandingDate',
  PaintBrandingPlacement = 'paintBrandingPlacement',
}

export enum PatientIdentifications {
  Microchip = 'microchip',
  Passport = 'passport',
  TagId = 'tagId',
  EarNotched = 'earNotched',
  TattoedId = 'tattoedId',
  FreezeMarking = 'freezeMarking',
  HotBrandedId = 'hotBrandedId',
  PaintBranding = 'paintBranding',
}

export enum PatientTypes {
  SmallAnimal = 'SMALL_ANIMAL',
  FarmProduction = 'FARM_PRODUCTION',
  Equine = 'EQUINE',
  ExoticOther = 'EXOTIC_OTHER',
}

export enum PatientSubTypes {
  NotFoodProductionAnimal = 'NOT_FOOD_PRODUCTION_ANIMAL',
  ZooAnimal = 'ZOO_ANIMAL',
  LabResearchAnimal = 'LAB_RESEARCH_ANIMAL',
}

export enum PatientStatus {
  Archived = 'ARCHIVED',
  Deceased = 'DECEASED',
  Active = 'ACTIVE',
}

export enum PatientCPRStatusValues {
  CPR = 'CPR',
  ACPR = 'ACPR',
  DNR = 'DNR',
  NotAsked = 'NOT_ASKED',
}

export enum PatientBloodGroupValues {
  DEA11Negative = 'DEA_1_1_NEGATIVE',
  DEA11Positive = 'DEA_1_1_POSITIVE',
  A = 'A',
  B = 'B',
  AB = 'AB',
  C = 'C',
  D = 'D',
  K = 'K',
  P = 'P',
  Q = 'Q',
  U = 'U',
  T = 'T',
}

export const AvailablePatientSpecies = [
  'Cat',
  'Dog',
  'Bird',
  'Rabbit',
  'Horse',
] as const
export type PatientSpecies = (typeof AvailablePatientSpecies)[number]

export function usePatientDetailsForm(
  formElement: ReturnType<typeof useTemplateRef<HTMLFormElement>>,
  fieldVisibility?: FieldVisibility,
) {
  const tPatientDetails = useTranslation('pages.patient.details')
  const tCommonDateTimePicker = useTranslation('common.dateTimePicker')
  const { regexRules } = useFormValidation(formElement)
  const { pairedCustomFields, validationSchema: customFieldsValidationSchema } =
    useCustomFieldsForModel(ContentTypeModelName.Patient, 'customFields')

  // Default visibility - all fields visible
  const visibility = fieldVisibility || {
    avatar: true,
    name: true,
    nickname: true,
    cprStatus: true,
    dateOfBirth: true,
    species: true,
    breed: true,
    gender: true,
    bloodGroup: true,
    color: true,
    additionalIdentifier: true,
    farmStableName: true,
    farmStableLocation: true,
    idNumber: true,
    identification: true,
    primaryClinicLocation: true,
    privacySettings: true,
    statuses: true,
    dateOfDeath: true,
    reasonOfDeath: true,
    patientTypes: true,
  }

  const initialValues = ref<PatientDetailsFormSchema>({
    name: '',
    officialName: '',
    type: PatientTypes.SmallAnimal,
    subtype: [],
    statuses: [],
    reasonOfDeath: '',
    dateOfDeath: '',
    cprStatus: undefined,
    dateOfBirth: '',
    species: '',
    breed: '',
    gender: undefined,
    bloodGroup: undefined,
    color: '',
    additionalIdentifier: '',
    isPrivate: false,
    visibleForDepartments: [],
    visibleForDepartmentGroups: [],
    primaryClinicLocation: '',
    farmStableName: '',
    farmStableLocation: '',
    idNumber: '',
    microchip: '',
    microchipDate: '',
    microchipPlacement: '',
    passport: '',
    passportDate: '',
    tattoedId: '',
    tattoedIdDate: '',
    tattoedIdPlacement: '',
    freezeMarking: '',
    freezeMarkingDate: '',
    freezeMarkingPlacement: '',
    earNotched: '',
    earNotchedDate: '',
    earNotchedPlacement: '',
    tagId: '',
    tagIdDate: '',
    tagIdPlacement: '',
    hotBrandedId: '',
    hotBrandedIdDate: '',
    hotBrandedIdPlacement: '',
    paintBranding: '',
    paintBrandingDate: '',
    paintBrandingPlacement: '',
    customFields: {},
  })

  const departmentsVisibilityValidation = (
    data: Partial<PatientDetailsFormSchema>,
  ) => {
    // Skip validation if privacy settings are not visible
    if (!visibility.privacySettings || !data.isPrivate) {
      return true
    }

    // In case the patient is private, at least
    // one department or department group should be visible
    const hasDepartments =
      Array.isArray(data.visibleForDepartments) &&
      data.visibleForDepartments?.length
    const hasDepartmentGroups =
      Array.isArray(data.visibleForDepartmentGroups) &&
      data.visibleForDepartmentGroups?.length

    return hasDepartments || hasDepartmentGroups
  }

  const { handleErrors } = useFormValidation(formElement)
  const {
    values,
    handleSubmit,
    resetForm,
    resetField,
    setValues,
    setFieldValue,
    setErrors,
    errors,
  } = useForm<PatientDetailsFormSchema>({
    validationSchema: computed(() =>
      toTypedSchema(
        z
          .object({
            name: visibility.nickname
              ? z
                  .string()
                  .min(1, tPatientDetails('fields.name.validation.required'))
              : z.string().optional(),
            officialName: z.string().optional(),
            type: visibility.patientTypes
              ? z
                  .string()
                  .min(1, tPatientDetails('fields.type.validation.required'))
              : z.string().optional(),
            subtype: z.array(z.string()).optional(),
            statuses: z.array(z.string()).optional(),
            reasonOfDeath: z.string().optional(),
            dateOfDeath: z
              .string()
              .optional()
              .refine(
                (val) => !val || regexRules.ISODate.test(val),
                tCommonDateTimePicker('invalidFormat'),
              ),
            cprStatus: z.nativeEnum(PatientCPRStatusValues).optional(),
            dateOfBirth: z
              .string()
              .optional()
              .refine(
                (val) => !val || regexRules.ISODate.test(val),
                tCommonDateTimePicker('invalidFormat'),
              ),
            species: visibility.species
              ? z
                  .string()
                  .min(1, tPatientDetails('fields.species.validation.required'))
              : z.string().optional(),
            breed: z.string().optional(),
            gender: z.enum(patientSex).optional(),
            bloodGroup: z.preprocess(
              (val) => val || undefined,
              z.nativeEnum(PatientBloodGroupValues).optional(),
            ),
            color: z.string().optional(),
            additionalIdentifier: z.string().optional(),
            isPrivate: z.boolean().optional(),
            visibleForDepartments: z.array(z.string()).optional(),
            visibleForDepartmentGroups: z.array(z.string()).optional(),
            primaryClinicLocation: z.string().optional(),
            farmStableName: z.string().optional(),
            farmStableLocation: z.string().optional(),
            idNumber: z.string().optional(),
            microchip: z.string().optional(),
            microchipDate: z.preprocess(
              (val) => val || undefined,
              z
                .string()
                .optional()
                .refine(
                  (val) => !val || regexRules.ISODate.test(val),
                  tCommonDateTimePicker('invalidFormat'),
                ),
            ),
            microchipPlacement: z.string().optional(),
            passport: z.string().optional(),
            passportDate: z
              .string()
              .optional()
              .refine(
                (val) => !val || regexRules.ISODate.test(val),
                tCommonDateTimePicker('invalidFormat'),
              ),
            tattoedId: z.string().optional(),
            tattoedIdDate: z
              .string()
              .optional()
              .refine(
                (val) => !val || regexRules.ISODateTime.test(val),
                tCommonDateTimePicker('invalidFormat'),
              ),
            tattoedIdPlacement: z.string().optional(),
            freezeMarking: z.string().optional(),
            freezeMarkingDate: z
              .string()
              .optional()
              .refine(
                (val) => !val || regexRules.ISODateTime.test(val),
                tCommonDateTimePicker('invalidFormat'),
              ),
            freezeMarkingPlacement: z.string().optional(),
            earNotched: z.string().optional(),
            earNotchedDate: z
              .string()
              .optional()
              .refine(
                (val) => !val || regexRules.ISODateTime.test(val),
                tCommonDateTimePicker('invalidFormat'),
              ),
            earNotchedPlacement: z.string().optional(),
            tagId: z.string().optional(),
            tagIdDate: z
              .string()
              .optional()
              .refine(
                (val) => !val || regexRules.ISODateTime.test(val),
                tCommonDateTimePicker('invalidFormat'),
              ),
            tagIdPlacement: z.string().optional(),
            hotBrandedId: z.string().optional(),
            hotBrandedIdDate: z
              .string()
              .optional()
              .refine(
                (val) => !val || regexRules.ISODateTime.test(val),
                tCommonDateTimePicker('invalidFormat'),
              ),
            hotBrandedIdPlacement: z.string().optional(),
            paintBranding: z.string().optional(),
            paintBrandingDate: z
              .string()
              .optional()
              .refine(
                (val) => !val || regexRules.ISODateTime.test(val),
                tCommonDateTimePicker('invalidFormat'),
              ),
            paintBrandingPlacement: z.string().optional(),
            ...customFieldsValidationSchema.value.shape,
          })
          .refine(
            (data) =>
              !visibility.privacySettings ||
              departmentsVisibilityValidation(data),
            {
              message: tPatientDetails(
                'fields.departmentsVisibility.validation.required',
              ),
              path: ['visibleForDepartments'],
            },
          )
          .refine(
            (data) =>
              !visibility.privacySettings ||
              departmentsVisibilityValidation(data),
            {
              message: tPatientDetails(
                'fields.departmentsVisibility.validation.required',
              ),
              path: ['visibleForDepartmentGroups'],
            },
          )
          .refine(
            (data) => {
              if (!visibility.farmStableName || !visibility.patientTypes) {
                return true
              }

              return (
                data.type === PatientTypes.SmallAnimal ||
                data.farmStableName?.trim()?.length
              )
            },
            {
              message: tPatientDetails(
                'fields.farmStableName.validation.required',
              ),
              path: ['farmStableName'],
            },
          ),
      ),
    ),
    initialValues: initialValues.value,
  })

  const onSubmit = async (
    event: Event,
    onSuccessCallback?: () => Promise<void>,
  ) => {
    event.preventDefault()

    await handleSubmit(
      // On validation success:
      async (data) => {
        // Success callback
        try {
          // Send data to the server
          if (onSuccessCallback) {
            await onSuccessCallback()
          }

          // After successful submission, reset the form with the new data
          // Make sure that missing fields get filled in object with `undefined` as value:
          for (const key in values) {
            if (!(key in data)) {
              ;(data as Record<string, unknown>)[key] = undefined
            }
          }

          // Update initial values with the new data
          initialValues.value = {
            ...initialValues.value,
            ...data,
          }
          await nextTick()
          resetForm({
            values: initialValues.value,
          })
        } catch (e) {
          console.error(e)
        }
      },
      // On validation error:
      () => handleErrors(),
    )()
  }

  const formValuesToModelAttributes = (): PatientCreate => {
    const {
      name,
      gender,
      dateOfBirth,
      bloodGroup,
      microchip,
      microchipDate,
      passport,
      color,
      additionalIdentifier,
      cprStatus,
      farmStableName,
      dateOfDeath,
      reasonOfDeath,
      statuses,
      species,
      breed,
      customFields,
    } = values as PatientDetailsFormSchema

    const isDeceased = statuses?.includes('DECEASED') && dateOfDeath

    return {
      name,
      gender: gender || 'UNKNOWN',
      date_of_birth: dateOfBirth || undefined,
      blood_group: bloodGroup || undefined,
      microchip: microchip || undefined,
      microchip_date: microchipDate || undefined,
      passport_number: passport || undefined,
      color: color || undefined,
      additional_identification: additionalIdentifier || undefined,
      archived: statuses?.includes('ARCHIVED') || false,
      not_for_food: false,
      cpr_agreement: cprStatus || PatientCPRStatusValues.NotAsked,
      stable_name: farmStableName || undefined,
      deceased: isDeceased ? dateOfDeath : undefined,
      reason_of_death: isDeceased && reasonOfDeath ? reasonOfDeath : undefined,
      species_id: Number(species),
      breed_id: breed ? Number(breed) : undefined,
      custom_fields: customFields || undefined,
    }
  }

  return {
    initialValues,
    values: values as PatientDetailsFormSchema,
    onSubmit,
    resetForm,
    resetField,
    setValues,
    setFieldValue,
    setErrors,
    errors,
    formValuesToModelAttributes,
    pairedCustomFields,
  }
}

export function usePatientDetailsFormFieldData() {
  const LOCALE_DATE_FORMAT = useCurrentLocaleDateFormat()
  const tPatientCPR = useTranslation('common.patientCPR')
  const tPatientPrivacyStatus = useTranslation('common.patientPrivacyStatus')
  const { translate: tEnumSex } = useEnumSexTranslation()
  const { departmentsListOptions, departmentGroupsListOptions } = useAuth()
  const { reasonOfDeathOptions, patientTypeOptions, getPatientSubtypeOptions } =
    usePatientDetailsFormFieldOptions()

  // PatientCPR Status
  const getPatientCPRAgreementStatus = (
    agreement?: PatientCPRStatus | undefined,
  ) => {
    const key = agreement || PatientCPRStatusValues.NotAsked
    let icon, iconColor, color, description

    const tooltips = {
      [PatientCPRStatusValues.CPR]: tPatientCPR('tooltipCPR'),
      [PatientCPRStatusValues.ACPR]: tPatientCPR('tooltipACPR'),
      [PatientCPRStatusValues.DNR]: tPatientCPR('tooltipDNR'),
      [PatientCPRStatusValues.NotAsked]: tPatientCPR('tooltipNotAsked'),
    }

    switch (key) {
      case PatientCPRStatusValues.CPR:
        description = tooltips[key]
        color = 'var(--n-color-status-success)'
        iconColor = 'var(--n-color-status-success)'
        icon = 'interface-cpr-acpr'
        break
      case PatientCPRStatusValues.ACPR:
        description = tooltips[key]
        color = 'var(--n-color-status-info)'
        iconColor = 'var(--n-color-status-info)'
        icon = 'interface-cpr-acpr'
        break
      case PatientCPRStatusValues.DNR:
        description = tooltips[key]
        color = 'var(--n-color-status-danger)'
        iconColor = 'var(--n-color-status-danger)'
        icon = 'interface-dnr'
        break
      case PatientCPRStatusValues.NotAsked:
      default:
        description = tPatientCPR('tooltipUnknownStatus')
        color = 'var(--n-color-text-weakest)'
        iconColor = 'var(--n-color-text-weakest)'
        icon = 'interface-cpr-unknown'
        break
    }

    return {
      key,
      description,
      tooltip: tooltips[key],
      color,
      iconColor,
      icon,
    }
  }

  // Patient Sex
  const getPatientSexIcon = (gender: PatientDetailsFormSchema['gender']) => {
    let icon = undefined
    switch (gender) {
      case 'FEMALE':
      case 'MARE':
        icon = 'medical-female'
        break
      case 'FEMALE_FIXED':
        icon = 'medical-spayed'
        break
      case 'GELDING':
      case 'MALE_FIXED':
        icon = 'medical-neutered'
        break
      case 'MALE':
      case 'STALLION':
        icon = 'medical-male'
        break
      case 'UNKNOWN':
      default:
        icon = undefined
    }

    return icon
  }
  const getPatientSex = (gender: PatientDetailsFormSchema['gender']) => ({
    label: gender ? tEnumSex(gender as PatientSex) : '',
    icon: gender ? getPatientSexIcon(gender || '') : undefined,
  })

  // Primary Clinic Location
  const getPatientPrimaryClinicLocation = (
    primaryClinicLocation: PatientDetailsFormSchema['primaryClinicLocation'],
  ) =>
    departmentsListOptions.find(
      (option) => option.value === primaryClinicLocation,
    )?.label

  // Patient Departments Visibility
  const getPatientDepartmentsVisibility = (
    visibleForDepartments: PatientDetailsFormSchema['visibleForDepartments'],
  ) =>
    visibleForDepartments?.map((departmentId: Option['value']) => {
      const departmentOption = departmentsListOptions?.find(
        (option) => option.value === departmentId,
      )

      return departmentOption?.label
    })

  // Patient Department Groups Visibility
  const getPatientDepartmentGroupsVisibility = (
    visibleForDepartmentGroups: PatientDetailsFormSchema['visibleForDepartmentGroups'],
  ) =>
    visibleForDepartmentGroups?.map((departmentGroupId: Option['value']) => {
      const departmentGroupOption = departmentGroupsListOptions?.find(
        (option) => option.value === departmentGroupId,
      )

      return departmentGroupOption?.label
    })

  // Patient Types and Subtypes
  const getPatientTypesAndSubtypes = (
    type: PatientDetailsFormSchema['type'],
    subtype: PatientDetailsFormSchema['subtype'] = [],
  ) => {
    const selectedPatientTypeLabel = patientTypeOptions.find(
      (optionType) => optionType.value === (type as PatientTypes),
    )?.label

    const selectedPatientSubTypesLabels = getPatientSubtypeOptions(type)
      ?.filter((optionSubtype) => subtype?.includes(optionSubtype.value))
      ?.map((optionSubtype) => optionSubtype.label)

    return [selectedPatientTypeLabel, ...selectedPatientSubTypesLabels].filter(
      Boolean,
    )
  }

  const getPatientPrivacyStatusBadge = (
    isPrivate: PatientDetailsFormSchema['isPrivate'],
  ) =>
    isPrivate
      ? tPatientPrivacyStatus('fields.privacyStatus.values.private')
      : tPatientPrivacyStatus('fields.privacyStatus.values.public')

  // Patient Reason of Death
  const getPatientReasonOfDeath = (
    reasonOfDeath: PatientDetailsFormSchema['reasonOfDeath'],
  ) => {
    const reasonOfDeathOption = reasonOfDeathOptions.find(
      (option) => option.value === reasonOfDeath,
    )

    return reasonOfDeathOption?.label
  }

  // Patient Date of Death
  const getPatientDateOfDeath = (
    dateOfDeath: PatientDetailsFormSchema['dateOfDeath'],
  ) => (dateOfDeath ? formatDate(dateOfDeath, LOCALE_DATE_FORMAT) : '')

  // Patient Species
  const speciesIcons = {
    Cat: 'generic-feline', // cat, lynx, leopard, ocelot
    Dog: 'generic-canine', // dog, fox, wolf, jackal, coyote
    Bird: 'generic-birds', // parakeets, lovebirds, canaries, parrots
    Rabbit: 'generic-leporidae', // rabbit, hare
    Horse: 'generic-equidae',
  } satisfies Record<PatientSpecies, string>
  const getPatientSpeciesIcon = (species: PatientSpecies) => {
    return (
      speciesIcons?.[species as keyof typeof speciesIcons] || 'generic-pet-paw'
    )
  }

  return {
    speciesIcons,
    getPatientSex,
    getPatientSexIcon,
    getPatientSpeciesIcon,
    getPatientCPRAgreementStatus,
    getPatientPrimaryClinicLocation,
    getPatientTypesAndSubtypes,
    getPatientPrivacyStatusBadge,
    getPatientDepartmentsVisibility,
    getPatientDepartmentGroupsVisibility,
    getPatientReasonOfDeath,
    getPatientDateOfDeath,
  }
}

/**
 * @param minimalVersion - When true, returns only Small Animal patient type option. When false, returns all patient type options including Farm Production, Equine, and Exotic/Other.
 */
export function usePatientDetailsFormFieldOptions(
  minimalVersion: boolean = false,
) {
  const tPatientType = useTranslation('common.patientType')

  // Species
  const speciesData = useSpeciesQuery({
    // After the species are fetched once, we don't need to have automatic refetching.
    // This is a workaround for pages where multiple patients are being displayed and
    // we don't want to refetch the species data on every single patient load.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  })
  const speciesOptions = computed(
    () =>
      speciesData.data?.data?.map((species) => ({
        label: species.attributes?.name || '',
        value: species.id as PatientSpecies,
        icon: species.attributes?.species_icon || '',
      })) || [],
  )

  // Blood Group
  const bloodGroupOptions = [
    { label: 'A', value: PatientBloodGroupValues.A },
    { label: 'B', value: PatientBloodGroupValues.B },
    { label: 'AB', value: PatientBloodGroupValues.AB },
    { label: 'C', value: PatientBloodGroupValues.C },
    { label: 'D', value: PatientBloodGroupValues.D },
    { label: 'K', value: PatientBloodGroupValues.K },
    { label: 'P', value: PatientBloodGroupValues.P },
    { label: 'Q', value: PatientBloodGroupValues.Q },
    { label: 'U', value: PatientBloodGroupValues.U },
    { label: 'T', value: PatientBloodGroupValues.T },
    { label: 'DEA 1.1 Positive', value: PatientBloodGroupValues.DEA11Positive },
    { label: 'DEA 1.1 Negative', value: PatientBloodGroupValues.DEA11Negative },
  ]

  // Farm/Stable name
  const farmStableNameOptions = [
    { label: 'Eagle Nest', value: 'EAGLE_NEST' },
    { label: 'Horse Haven', value: 'HORSE_HAVEN' },
  ]

  // Farm/Stable location
  const farmStableLocationOptions = [
    { label: 'E-002', value: 'E_002' },
    { label: 'C-PR3', value: 'C_PR3' },
  ]

  // Patient Types
  const minimalPatientTypeOptions = [
    {
      label: tPatientType('fields.type.options.smallAnimal'),
      value: PatientTypes.SmallAnimal,
    },
  ]
  const patientTypeOptions: {
    label: string
    value: PatientTypes
  }[] = minimalVersion
    ? minimalPatientTypeOptions
    : [
        ...minimalPatientTypeOptions,
        {
          label: tPatientType('fields.type.options.farmProduction'),
          value: PatientTypes.FarmProduction,
        },
        {
          label: tPatientType('fields.type.options.equine'),
          value: PatientTypes.Equine,
        },
        {
          label: tPatientType('fields.type.options.exoticOther'),
          value: PatientTypes.ExoticOther,
        },
      ]

  // Patient Subtypes
  const getPatientSubtypeOptions = (type: PatientDetailsFormSchema['type']) => [
    ...(type === PatientTypes.SmallAnimal
      ? [
          {
            label: tPatientType(
              'fields.subtype.options.notFoodProductionAnimal',
            ),
            value: PatientSubTypes.NotFoodProductionAnimal,
          },
        ]
      : []),
    {
      label: tPatientType('fields.subtype.options.zooAnimal'),
      value: PatientSubTypes.ZooAnimal,
    },
    {
      label: tPatientType('fields.subtype.options.labResearchAnimal'),
      value: PatientSubTypes.LabResearchAnimal,
    },
  ]

  // Reason of Death
  const reasonOfDeathOptions = [
    { label: 'Consumed too much lasagna', value: 'CONSUMED_TOO_MUCH_LASAGNA' },
    { label: 'Orange tabby', value: 'ORANGE_TABBY' },
  ]

  return {
    speciesOptions,
    bloodGroupOptions,
    farmStableNameOptions,
    farmStableLocationOptions,
    reasonOfDeathOptions,
    patientTypeOptions,
    getPatientSubtypeOptions,
  }
}

/**
 * A composable that handles the logic for displaying the
 * additional identifier field in the patient details form
 */
export function usePatientDetailsAdditionalIdentifier(
  value: PatientDetailsFormSchema['additionalIdentifier'],
  setFieldValue: FormContext<PatientDetailsFormSchema>['setFieldValue'],
) {
  const displayAdditionalIdentifier = ref(Boolean(value))
  const removeAdditionalIdentifier = () => {
    setFieldValue('additionalIdentifier', '')
    displayAdditionalIdentifier.value = false
  }
  watch(
    () => value,
    () => {
      if (value && !displayAdditionalIdentifier.value) {
        displayAdditionalIdentifier.value = true
      }
    },
  )

  return {
    displayAdditionalIdentifier,
    removeAdditionalIdentifier,
  }
}

/**
 * A composable that centralizes the shared logic and state
 * for patient identification form fields
 */
export function usePatientIdentification(patientType?: MaybeRef<PatientTypes>) {
  const tPatientIdentification = useTranslation('common.patientIdentification')

  // Get all the keys for the identification fields
  const identificationFieldsKeys = Object.values(PatientIdentificationFields)

  // Create a reactive object to store the form fields.
  // Each field is expected to have useField's `value` and `setValue` function
  const formIdentificationFields = ref(
    {} as Record<
      (typeof identificationFieldsKeys)[number],
      {
        value: PatientDetailsFormSchema[(typeof identificationFieldsKeys)[number]]
        setValue: (
          value: PatientDetailsFormSchema[(typeof identificationFieldsKeys)[number]],
        ) => void
      }
    >,
  )
  onBeforeMount(() => {
    // Get the values of the identification fields from the form
    identificationFieldsKeys.forEach((identificationFieldName) => {
      const { value, setValue } = useField<
        PatientDetailsFormSchema[typeof identificationFieldName]
      >(identificationFieldName)

      Object.assign(formIdentificationFields.value, {
        [identificationFieldName]: {
          value,
          setValue,
        },
      })
    })
  })

  // List of PatientTypes fields that contain values
  const formIdentificationFieldsWithValues = computed<
    (keyof PatientIdentificationValues)[]
  >(() =>
    identificationFieldsKeys.filter((key) =>
      Boolean(formIdentificationFields.value[key]?.value),
    ),
  )

  // Mapping of identification types to field metadata
  const fieldInfoMap: {
    [key in PatientIdentifications]: {
      label: string
      labelDate: string
      labelPlacement?: string
      name: keyof PatientIdentificationValues
      nameDate?: keyof PatientIdentificationValues
      namePlacement?: keyof PatientIdentificationValues
    }
  } = {
    [PatientIdentifications.Microchip]: {
      label: tPatientIdentification('fields.microchip.label'),
      labelDate: tPatientIdentification('dateLabel'),
      labelPlacement: tPatientIdentification('placementLabel'),
      name: PatientIdentifications.Microchip,
      nameDate: `${PatientIdentifications.Microchip}Date`,
      // namePlacement: `${PatientIdentifications.Microchip}Placement`, // @todo: add when API is ready
    },
    [PatientIdentifications.Passport]: {
      label: tPatientIdentification('fields.passport.label'),
      labelDate: tPatientIdentification('issueDateLabel'),
      name: PatientIdentifications.Passport,
      // nameDate: `${PatientIdentifications.Passport}Date`, // @todo: add when API is ready
    },
    [PatientIdentifications.TagId]: {
      label: tPatientIdentification('fields.tagId.label'),
      labelDate: tPatientIdentification('dateLabel'),
      labelPlacement: tPatientIdentification('placementLabel'),
      name: PatientIdentifications.TagId,
      nameDate: `${PatientIdentifications.TagId}Date`,
      namePlacement: `${PatientIdentifications.TagId}Placement`,
    },
    [PatientIdentifications.EarNotched]: {
      label: tPatientIdentification('fields.earNotched.label'),
      labelDate: tPatientIdentification('dateLabel'),
      labelPlacement: tPatientIdentification('placementLabel'),
      name: PatientIdentifications.EarNotched,
      nameDate: `${PatientIdentifications.EarNotched}Date`,
      namePlacement: `${PatientIdentifications.EarNotched}Placement`,
    },
    [PatientIdentifications.TattoedId]: {
      label: tPatientIdentification('fields.tattoedId.label'),
      labelDate: tPatientIdentification('dateLabel'),
      labelPlacement: tPatientIdentification('placementLabel'),
      name: PatientIdentifications.TattoedId,
      nameDate: `${PatientIdentifications.TattoedId}Date`,
      namePlacement: `${PatientIdentifications.TattoedId}Placement`,
    },
    [PatientIdentifications.FreezeMarking]: {
      label: tPatientIdentification('fields.freezeMarking.label'),
      labelDate: tPatientIdentification('dateLabel'),
      labelPlacement: tPatientIdentification('placementLabel'),
      name: PatientIdentifications.FreezeMarking,
      nameDate: `${PatientIdentifications.FreezeMarking}Date`,
      namePlacement: `${PatientIdentifications.FreezeMarking}Placement`,
    },
    [PatientIdentifications.HotBrandedId]: {
      label: tPatientIdentification('fields.hotBrandedId.label'),
      labelDate: tPatientIdentification('dateLabel'),
      labelPlacement: tPatientIdentification('placementLabel'),
      name: PatientIdentifications.HotBrandedId,
      nameDate: `${PatientIdentifications.HotBrandedId}Date`,
      namePlacement: `${PatientIdentifications.HotBrandedId}Placement`,
    },
    [PatientIdentifications.PaintBranding]: {
      label: tPatientIdentification('fields.paintBranding.label'),
      labelDate: tPatientIdentification('dateLabel'),
      labelPlacement: tPatientIdentification('placementLabel'),
      name: PatientIdentifications.PaintBranding,
      nameDate: `${PatientIdentifications.PaintBranding}Date`,
      namePlacement: `${PatientIdentifications.PaintBranding}Placement`,
    },
  }

  // Determines the default identification types based on the passed patient type
  const defaultIdentificationTypes = computed(() => {
    const defaultIdentificationTypesMap: Record<
      PatientTypes,
      PatientIdentifications[]
    > = {
      [PatientTypes.SmallAnimal]: [
        PatientIdentifications.Microchip,
        // PatientIdentifications.TagId, // @todo: add when API is ready
        PatientIdentifications.Passport,
      ],
      [PatientTypes.FarmProduction]: [
        // PatientIdentifications.TagId, // @todo: add when API is ready
        PatientIdentifications.TattoedId,
      ],
      [PatientTypes.Equine]: [
        PatientIdentifications.Microchip,
        // PatientIdentifications.TagId, // @todo: add when API is ready
        PatientIdentifications.Passport,
      ],
      [PatientTypes.ExoticOther]: [
        PatientIdentifications.Microchip,
        // PatientIdentifications.TagId, // @todo: add when API is ready
        PatientIdentifications.TattoedId,
        PatientIdentifications.Passport,
      ],
    }

    if (!patientType) {
      return []
    }
    return defaultIdentificationTypesMap[unref(patientType)] || []
  })

  // Determines the "other" identification types (those that are not in the default list)
  const otherIdentificationsTypes = computed(() => {
    const allTypes = Object.values(PatientIdentifications)
    return allTypes.filter(
      (type) => !defaultIdentificationTypes.value.includes(type),
    )
  })

  // Return field information by identification type
  const getFieldInfo = (type: PatientIdentifications) => {
    return fieldInfoMap[type]
  }

  // Given the form’s fields that have values, return any “other” types that are set
  const getOtherIdentificationTypesWithValues =
    (): PatientIdentifications[] => {
      const typesWithValues = formIdentificationFieldsWithValues.value.reduce<
        PatientIdentifications[]
      >((acc, key) => {
        // Determine the type by checking the start of the key (case-insensitive)
        const matchingType = Object.values(PatientIdentifications).find(
          (type) => key.toLowerCase().startsWith(type.toLowerCase()),
        )
        if (matchingType) {
          acc.push(matchingType)
        }
        return acc
      }, [])
      return Array.from(new Set(typesWithValues)).filter(
        (type) => !defaultIdentificationTypes.value.includes(type),
      )
    }

  // State for the additional (“other”) identification types
  const selectedOtherTypes = ref<PatientIdentifications[]>([])
  onMounted(() => {
    selectedOtherTypes.value = getOtherIdentificationTypesWithValues()
  })

  // Get the list of available options for "other" fields
  const getOtherOptions = (fieldToSkip?: PatientIdentifications) => {
    const activeValues = new Set([
      ...defaultIdentificationTypes.value,
      ...selectedOtherTypes.value.filter((opt) => opt !== fieldToSkip),
    ])
    return otherIdentificationsTypes.value
      .filter((option) => !activeValues.has(option))
      .map((option) => ({
        label: fieldInfoMap[option].label,
        value: option,
      }))
  }

  // Whether adding an extra field is allowed
  const allowAddingOtherField = computed(() => getOtherOptions()?.length)

  // Add a new "other" field if an option is available
  const addOtherField = () => {
    const options = getOtherOptions()
    if (options.length) {
      selectedOtherTypes.value.push(options[0].value)
    }
  }

  // Remove field and clear its values
  const removeField = async (field: PatientIdentifications) => {
    const index = selectedOtherTypes.value.indexOf(field)
    if (index !== -1) {
      selectedOtherTypes.value.splice(index, 1)
    }
    await nextTick()
    const fieldInfo = getFieldInfo(field)
    formIdentificationFields.value[fieldInfo.name].setValue('')
    if (fieldInfo.nameDate) {
      formIdentificationFields.value[fieldInfo.nameDate].setValue('')
    }
    if (fieldInfo.namePlacement) {
      formIdentificationFields.value[fieldInfo.namePlacement].setValue('')
    }
  }

  return {
    fieldInfoMap,
    defaultIdentificationTypes,
    otherIdentificationsTypes,
    formIdentificationFields,
    formIdentificationFieldsWithValues,
    selectedOtherTypes,
    allowAddingOtherField,
    getFieldInfo,
    getOtherOptions,
    removeField,
    addOtherField,
  }
}
